import { differenceInDays } from "date-fns"
import type { Tool } from "~/.generated/prisma/client"
import { submissionsConfig } from "~/config/submissions"

function isGitHubPrimaryWebsite(websiteUrl: string): boolean {
  try {
    const { hostname, protocol } = new URL(websiteUrl)

    if (protocol !== "http:" && protocol !== "https:") {
      return false
    }

    return hostname === "github.com" || hostname === "www.github.com"
  } catch {
    return false
  }
}

/**
 * Repository-first tools use GitHub as their primary website and do not need
 * product screenshots on cards or detail pages.
 */
export const getToolScreenshotUrl = (tool: {
  screenshotUrl: string | null
  websiteUrl: string
  repositories?: readonly unknown[]
}) => {
  if (!tool.screenshotUrl) {
    return null
  }

  if (tool.repositories?.length && isGitHubPrimaryWebsite(tool.websiteUrl)) {
    return null
  }

  return tool.screenshotUrl
}

/**
 * Check if a tool is published.
 *
 * @param tool - The tool to check.
 * @returns Whether the tool is published.
 */
export const isToolPublished = (tool: Pick<Tool, "status">) => {
  return ["Published"].includes(tool.status)
}

/**
 * Check if a tool is scheduled.
 *
 * @param tool - The tool to check.
 * @returns Whether the tool is scheduled.
 */
export const isToolScheduled = (tool: Pick<Tool, "status">) => {
  return ["Scheduled"].includes(tool.status)
}

/**
 * Check if a tool is approved (scheduled or published)
 *
 * @param tool - The tool to check.
 * @returns Whether the tool is published.
 */
export const isToolApproved = (tool: Pick<Tool, "status">) => {
  return ["Scheduled", "Published"].includes(tool.status)
}

/**
 * Check if a tool is within the expedite threshold.
 *
 * @param tool - The tool to check.
 * @returns Whether the tool is within the expedite threshold.
 */
export const isToolWithinExpediteThreshold = (tool: Pick<Tool, "publishedAt">) => {
  const threshold = submissionsConfig.expediteThreshold

  return tool.publishedAt && differenceInDays(tool.publishedAt, new Date()) < threshold
}
